import React from 'react';
import classnames from 'classnames';

import {
  Collapse,
  Chip,
  Typography,
  List,
  ListItem,
  ListSubheader,
  Link,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Item, MenuItem } from '@hydra/common/components/Header/menuItems';
import { Icon, Translate } from '@hydra/common/components';

import getTranslatedUrl from '@hydra/utils/getTranslatedUrl';
import { sendEvent } from '@hydra/utils/gtm';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles((theme) => ({
  header: {
    pointerEvents: 'none',
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightSemibold,
      fontSize: theme.spacing(1.375),
      color: theme.palette.grey[500],
      textTransform: 'uppercase'
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    },
    textTransform: 'uppercase',
    padding: theme.spacing(0, 2)
  },
  item: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.primary,
    fontSize: theme.spacing(2.375)
  },
  itemText: {
    display: 'inline-flex',
    fontSize: theme.spacing(2.375)
  },
  groupItem: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  activeGroupItem: {
    backgroundColor: theme.palette.background.secondary,
    '&:hover': {
      backgroundColor: theme.palette.background.secondary
    }
  },
  groupContainer: {
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: `0 0 ${theme.spacing(1.6)}px ${theme.spacing(1.6)}px`,
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[4],
    borderTop: 'none'
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.fontSizeSmall,
    height: theme.spacing(2.25),
    alignSelf: 'center'
  },
  helpDropdownButton: {
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.primary
  },
  divider: { backgroundColor: theme.palette.background.secondary }
}));

interface INestedListProps {
  menuOpen: string | null;
  setMenuOpen: React.Dispatch<React.SetStateAction<string | null>>;
  itemKey: string;
  contentColor: 'white' | 'gray';
  menuItems?: Item[][];
  menu: MenuItem;
}

export default function NestedList ({
  menuOpen,
  setMenuOpen,
  itemKey,
  contentColor,
  menuItems,
  menu
}: INestedListProps) {
  const classes = useStyles();
  const { locale } = useI18n();

  function handleGtmClick (label = '') {
    if (label) {
      sendEvent(label);
    }
  }

  function handleClick () {
    if (menuOpen === itemKey) {
      setMenuOpen(null);
    } else {
      setMenuOpen(itemKey);
    }
  }

  return (
    <>
      <ListItem
        button
        onClick={handleClick}
        className={classnames(
          classes.groupItem,
          {
            [classes.activeGroupItem]: menuOpen === itemKey
          }
        )}
      >
        <Typography noWrap className={classes.helpDropdownButton}>
          <Translate content={menu.label} />
        </Typography>
        {menuOpen === itemKey
          ? (
            <Icon type='chevron-up' color={contentColor} />
            )
          : (
            <Icon type='chevron-down' color={contentColor} />
            )}
      </ListItem>
      <Collapse in={menuOpen === itemKey} timeout='auto' unmountOnExit>
        <List
          component='div'
          disablePadding
          className={classes.groupContainer}
        >
          {menuItems && menuItems.map((items: Item[], gIndex: number) => (
            <List disablePadding key={gIndex}>
              {items.map((item: Item, index: number) => item.header
                ? (
                  <div key={index}>
                    {gIndex !== 0 && <Divider className={classes.divider} />}
                    <ListSubheader component='div' className={classes.header}>
                      <Translate content={item.label} />
                    </ListSubheader>
                  </div>
                  )
                : (
                  <ListItem
                    key={index}
                    href={
                      item.externalLink
                        ? item.externalLink
                        : item.link && getTranslatedUrl(item.link, locale)
                    }
                    button
                    component={Link}
                    onClick={() => {
                      handleGtmClick(item.gtmLabel);
                    }}
                    className={classes.item}
                  >
                    <Typography noWrap component='span' className={classes.itemText}>
                      <Translate content={item.label} />
                      {item.chipTag && (
                        <Chip
                          label={<Translate content={item.chipTag} />}
                          className={classes.chip}
                          color='primary'
                          size='small'
                        />
                      )}
                    </Typography>
                  </ListItem>
                  )
              )}
            </List>
          ))}
        </List>
      </Collapse>
    </>
  );
}
